import React, { useMemo, useState } from 'react';
import { Mail, Phone, MapPin, Clock, AlertCircle, CheckCircle2, MessageSquare, ExternalLink, Share2 } from 'lucide-react';
import { submitContactMessage } from '../api/client';
import { getTranslation as t, translateError as te, isRTL } from '../utils/translations';
import { SOCIAL_PLATFORMS } from '../components/SocialIcons';

const socialEntries = (social = {}) => {
    const all = [
        ['TikTok', social.tiktok],
        ['Facebook', social.facebook],
        ['Instagram', social.instagram],
        ['WhatsApp', social.whatsapp]
    ];
    return all.filter(([, value]) => String(value || '').trim());
};

const ContactPage = ({ storeSettings }) => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const contact = storeSettings?.contactDetails || {};
    const social = storeSettings?.socialMedia || {};

    const resolvedContactEmail = useMemo(() => {
        return storeSettings?.contactFormRecipient || contact.email || social.email || '';
    }, [contact.email, social.email, storeSettings?.contactFormRecipient]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError('');
        setSuccess('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setError(te('Please fill your name, email, and message.'));
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            setSuccess('');
            await submitContactMessage({
                ...form,
                targetEmail: resolvedContactEmail,
                pageUrl: typeof window !== 'undefined' ? window.location.href : '/contact'
            });
            setSuccess(te('Your message has been sent successfully. Our team will contact you soon.'));
            setForm({ name: '', email: '', subject: '', message: '' });
        } catch (err) {
            console.error('Failed to send contact message:', err);
            setError(te(err?.response?.data?.message || 'Failed to send your message. Please try again.'));
        } finally {
            setSubmitting(false);
        }
    };

    const isArabic = isRTL();

    const phoneNum = contact.phone || social.phone || "+966 50 000 0000";
    const emailAddr = contact.email || social.email || "concierge@luxvono.com";
    const waNum = social.whatsapp || contact.phone || "";
    const waLink = waNum ? `https://wa.me/${waNum.replace(/[^0-9]/g, '')}` : "";
    const officeAddr = contact.address || (isArabic ? "مكتب كونسيرج كبار الشخصيات، الرياض، المملكة العربية السعودية" : "VIP Concierge Office, Riyadh, Saudi Arabia");
    const hours = contact.supportHours || (isArabic ? "خدمة العملاء VIP على مدار الساعة" : "24/7 Dedicated VIP Support");

    return (
        <div className="lux-contact-container" dir={isArabic ? 'rtl' : 'ltr'} style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '40px 20px 80px 20px',
            textAlign: isArabic ? 'right' : 'left'
        }}>
            <style>{`
                .lux-contact-header {
                    text-align: center;
                    margin-bottom: 60px;
                }
                .lux-contact-title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    color: var(--text-primary, #111);
                    margin-bottom: 14px;
                    letter-spacing: -0.5px;
                }
                .lux-contact-subtitle {
                    font-size: 1.15rem;
                    color: var(--text-secondary, #666);
                    max-width: 600px;
                    margin: 0 auto;
                    line-height: 1.6;
                }
                .lux-contact-grid {
                    display: grid;
                    grid-template-columns: 1fr 1.2fr;
                    gap: 50px;
                    align-items: start;
                }
                @media (max-width: 900px) {
                    .lux-contact-grid {
                        grid-template-columns: 1fr;
                        gap: 40px;
                    }
                }
                .lux-contact-info-panel {
                    display: flex;
                    flex-direction: column;
                    gap: 24px;
                }
                .lux-concierge-card {
                    background: var(--bg-card, #ffffff);
                    border: 1px solid var(--border-color, #eaeaea);
                    border-radius: 20px;
                    padding: 24px;
                    display: flex;
                    gap: 20px;
                    align-items: flex-start;
                    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.01);
                }
                .lux-concierge-card:hover {
                    border-color: rgba(197, 168, 128, 0.5);
                    box-shadow: 0 12px 30px rgba(197, 168, 128, 0.08);
                    transform: translateY(-2px);
                }
                .lux-concierge-icon-wrap {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    background: rgba(197, 168, 128, 0.1);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .lux-concierge-details {
                    flex: 1;
                }
                .lux-concierge-label {
                    font-size: 11px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    color: #C5A880;
                    margin-bottom: 6px;
                }
                .lux-concierge-value {
                    font-size: 1.1rem;
                    font-weight: 700;
                    color: var(--text-primary, #111);
                    margin-bottom: 4px;
                    word-break: break-all;
                }
                .lux-concierge-value a {
                    color: inherit;
                    text-decoration: none;
                    transition: color 0.2s;
                }
                .lux-concierge-value a:hover {
                    color: #C5A880;
                }
                .lux-concierge-subtext {
                    font-size: 0.85rem;
                    color: var(--text-secondary, #666);
                    line-height: 1.5;
                }
                .lux-contact-form-panel {
                    background: var(--bg-card, #ffffff);
                    border: 1px solid var(--border-color, #eaeaea);
                    border-radius: 24px;
                    padding: 40px;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.02);
                }
                @media (max-width: 600px) {
                    .lux-contact-form-panel {
                        padding: 24px;
                    }
                }
                .lux-form-title {
                    font-size: 1.5rem;
                    font-weight: 800;
                    color: var(--text-primary, #111);
                    margin-bottom: 8px;
                }
                .lux-form-subtitle {
                    font-size: 0.9rem;
                    color: var(--text-secondary, #666);
                    margin-bottom: 30px;
                }
                .lux-contact-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                .lux-input-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .lux-input-label {
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    color: var(--text-primary, #111);
                }
                .lux-input, .lux-textarea {
                    width: 100%;
                    padding: 12px 16px;
                    border-radius: 10px;
                    background: var(--bg-highlight, #f9f9f9);
                    border: 1px solid var(--border-color, #eaeaea);
                    color: var(--text-primary, #111);
                    outline: none;
                    font-size: 14px;
                    transition: all 0.3s ease;
                    box-sizing: border-box;
                    font-family: inherit;
                }
                .lux-input:focus, .lux-textarea:focus {
                    background: var(--bg-card, #fff);
                    border-color: #C5A880;
                    box-shadow: 0 0 0 3px rgba(197, 168, 128, 0.15);
                }
                .lux-submit-btn {
                    background: linear-gradient(135deg, #C5A880, #A0875E) !important;
                    color: #000000 !important;
                    border: none !important;
                    padding: 14px 28px !important;
                    border-radius: 10px !important;
                    font-size: 13px !important;
                    font-weight: 700 !important;
                    cursor: pointer !important;
                    transition: all 0.2s ease !important;
                    text-transform: uppercase;
                    letter-spacing: 0.8px;
                    box-shadow: 0 4px 14px rgba(197, 168, 128, 0.25);
                }
                .lux-submit-btn:hover:not(:disabled) {
                    transform: translateY(-1px);
                    box-shadow: 0 6px 20px rgba(197, 168, 128, 0.35);
                }
                .lux-submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .lux-alert {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 14px 16px;
                    border-radius: 10px;
                    font-size: 13px;
                    font-weight: 500;
                }
                .lux-alert-error {
                    background: rgba(239, 68, 68, 0.08);
                    border: 1px solid rgba(239, 68, 68, 0.2);
                    color: #ef4444;
                }
                .lux-alert-success {
                    background: rgba(16, 185, 129, 0.08);
                    border: 1px solid rgba(16, 185, 129, 0.2);
                    color: #10b981;
                }
                
                /* RTL Overrides */
                [dir="rtl"] .lux-contact-title,
                [dir="rtl"] .lux-form-title,
                [dir="rtl"] .lux-concierge-value,
                [dir="rtl"] .lux-input-label,
                [dir="rtl"] .lux-submit-btn {
                    font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                }
                [dir="rtl"] .lux-contact-subtitle,
                [dir="rtl"] .lux-form-subtitle,
                [dir="rtl"] .lux-concierge-subtext,
                [dir="rtl"] .lux-alert,
                [dir="rtl"] .lux-input,
                [dir="rtl"] .lux-textarea {
                    font-family: 'Cairo', 'Noto Sans Arabic', sans-serif;
                    font-weight: 400;
                }
            `}</style>

            <div className="lux-contact-header">
                <h1 className="lux-contact-title">{t('CONTACT_TITLE')}</h1>
                <p className="lux-contact-subtitle">{t('CONTACT_SUBTITLE')}</p>
            </div>

            <div className="lux-contact-grid">
                {/* Left Side: Premium Concierge Contact Cards */}
                <section className="lux-contact-info-panel">
                    {/* Card 1: Concierge Phone */}
                    <div className="lux-concierge-card">
                        <div className="lux-concierge-icon-wrap">
                            <Phone size={20} style={{ color: '#C5A880' }} />
                        </div>
                        <div className="lux-concierge-details">
                            <span className="lux-concierge-label">{isArabic ? "كونسيرج الهاتف" : "Concierge Phone"}</span>
                            <h3 className="lux-concierge-value">
                                <a href={`tel:${phoneNum}`}>{phoneNum}</a>
                            </h3>
                            <p className="lux-concierge-subtext">
                                {isArabic ? "خط الاتصال الدولي والمساعد الشخصي المباشر" : "Direct international line for instant voice support"}
                            </p>
                        </div>
                    </div>

                    {/* Card 2: WhatsApp Chat */}
                    {waLink && (
                        <div className="lux-concierge-card">
                            <div className="lux-concierge-icon-wrap">
                                <MessageSquare size={20} style={{ color: '#C5A880' }} />
                            </div>
                            <div className="lux-concierge-details">
                                <span className="lux-concierge-label">{isArabic ? "محادثة واتساب الفورية" : "WhatsApp Concierge"}</span>
                                <h3 className="lux-concierge-value">
                                    <a href={waLink} target="_blank" rel="noreferrer">WhatsApp Chat</a>
                                </h3>
                                <p className="lux-concierge-subtext">
                                    {isArabic ? "استفسارات فورية سريعة وعرض كتالوج المنتجات" : "Instant message support and digital catalog browsing"}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Card 3: Email Inquiry */}
                    <div className="lux-concierge-card">
                        <div className="lux-concierge-icon-wrap">
                            <Mail size={20} style={{ color: '#C5A880' }} />
                        </div>
                        <div className="lux-concierge-details">
                            <span className="lux-concierge-label">{isArabic ? "المراسلة الإلكترونية" : "Email Relations"}</span>
                            <h3 className="lux-concierge-value">
                                <a href={`mailto:${emailAddr}`}>{emailAddr}</a>
                            </h3>
                            <p className="lux-concierge-subtext">
                                {isArabic ? "الطلبات والاستفسارات الرسمية وطلبات الاستيراد الخاص" : "Bespoke sourcing requests and official corporate inquiries"}
                            </p>
                        </div>
                    </div>

                    {/* Card 4: Office Address & Support Hours */}
                    <div className="lux-concierge-card">
                        <div className="lux-concierge-icon-wrap">
                            <Clock size={20} style={{ color: '#C5A880' }} />
                        </div>
                        <div className="lux-concierge-details">
                            <span className="lux-concierge-label">{isArabic ? "ساعات العمل والمكتب" : "Concierge Hours & Address"}</span>
                            <h3 className="lux-concierge-value" style={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.5 }}>
                                {hours}
                            </h3>
                            <p className="lux-concierge-subtext" style={{ marginTop: '4px' }}>
                                <MapPin size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle', color: '#C5A880' }} />
                                {officeAddr}
                            </p>
                        </div>
                    </div>

                    {/* Card 5: Social Media Official Channels */}
                    {SOCIAL_PLATFORMS.some(p => Boolean(String(social[p.key] || '').trim())) && (
                        <div className="lux-concierge-card" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div className="lux-concierge-icon-wrap">
                                    <Share2 size={20} style={{ color: '#C5A880' }} />
                                </div>
                                <div className="lux-concierge-details">
                                    <span className="lux-concierge-label">{isArabic ? "قنواتنا الرسمية الموثقة" : "Official Social Networks"}</span>
                                    <h3 className="lux-concierge-value" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                        {isArabic ? "متابعة وتواصل حصري" : "Follow & Engage"}
                                    </h3>
                                </div>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', paddingTop: '6px' }}>
                                {SOCIAL_PLATFORMS.filter(p => Boolean(String(social[p.key] || '').trim())).map(p => (
                                    <a
                                        key={p.key}
                                        href={social[p.key]}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="lux-social-pill"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            padding: '8px 14px',
                                            borderRadius: '9999px',
                                            background: p.bgColor,
                                            border: `1px solid ${p.borderColor}`,
                                            color: p.color,
                                            fontSize: '12px',
                                            fontWeight: '700',
                                            textDecoration: 'none',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        {p.icon(16)}
                                        <span>{isArabic ? p.labelAr : p.label}</span>
                                        <ExternalLink size={11} style={{ opacity: 0.7 }} />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </section>

                {/* Right Side: Redesigned Luxury Contact Form */}
                <section className="lux-contact-form-panel">
                    <h2 className="lux-form-title">{t('SEND_MESSAGE')}</h2>
                    <p className="lux-form-subtitle">
                        {isArabic ? "يرجى ملء النموذج أدناه وسيقوم كونسيرج لوكسفونو بالرد في غضون ساعتين." : "Fill out the fields below. A concierge specialist will respond within two hours."}
                    </p>
                    
                    <form className="lux-contact-form" onSubmit={handleSubmit}>
                        <div className="lux-input-group">
                            <label className="lux-input-label">{t('YOUR_NAME')}</label>
                            <input
                                className="lux-input"
                                type="text"
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                placeholder={isArabic ? "الاسم الكامل" : "Full name"}
                                required
                            />
                        </div>

                        <div className="lux-input-group">
                            <label className="lux-input-label">{t('EMAIL')}</label>
                            <input
                                className="lux-input"
                                type="email"
                                name="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder="you@example.com"
                                required
                            />
                        </div>

                        <div className="lux-input-group">
                            <label className="lux-input-label">{t('SUBJECT')}</label>
                            <input
                                className="lux-input"
                                type="text"
                                name="subject"
                                value={form.subject}
                                onChange={handleChange}
                                placeholder={isArabic ? "مثال: استفسار عن منتج، تتبع طلب..." : "e.g. Product inquiry, order status..."}
                            />
                        </div>

                        <div className="lux-input-group">
                            <label className="lux-input-label">{t('MESSAGE')}</label>
                            <textarea
                                className="lux-textarea"
                                rows={5}
                                name="message"
                                value={form.message}
                                onChange={handleChange}
                                placeholder={isArabic ? "كيف يمكننا مساعدتك اليوم؟" : "How can we assist you today?"}
                                required
                            />
                        </div>

                        {error && (
                            <div className="lux-alert lux-alert-error">
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        {success && (
                            <div className="lux-alert lux-alert-success">
                                <CheckCircle2 size={16} />
                                <span>{success}</span>
                            </div>
                        )}

                        <button type="submit" className="lux-submit-btn" disabled={submitting}>
                            {submitting ? t('SENDING') : t('SEND_MESSAGE')}
                        </button>
                    </form>
                </section>
            </div>
        </div>
    );
};

export default ContactPage;
